import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authApi } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  // user: null = checking, object = authenticated, false = not authenticated
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const data = await authApi.me();
      setUser(data.user);
      setEntitlements(data.entitlements || []);
      return data;
    } catch (e) {
      setUser(false);
      setEntitlements([]);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — AuthCallback exchanges the
    // session_id and establishes the session first, so skip the /me check here.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    await refresh();
    return data;
  };

  const register = async (payload) => {
    const data = await authApi.register(payload);
    await refresh();
    return data;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(false);
      setEntitlements([]);
    }
  };

  const logoutAll = async () => {
    try {
      await authApi.logoutAll();
    } finally {
      setUser(false);
      setEntitlements([]);
    }
  };

  const value = {
    user,
    entitlements,
    hasAccess: entitlements.length > 0,
    emailVerified: !!user && user.email_verified,
    login,
    register,
    logout,
    logoutAll,
    refresh,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
